import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { getUsers, createUser, updateUser, deleteUser } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Shield, Eye, KeyRound } from "lucide-react";

const ROLE_CONFIG = {
  admin: { label: "Admin", color: "bg-amber-100 text-amber-700", icon: Shield },
  full_access: { label: "Full Access", color: "bg-emerald-100 text-emerald-700", icon: KeyRound },
  view_only: { label: "View Only", color: "bg-slate-100 text-slate-600", icon: Eye },
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "view_only" });
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ name: "", email: "", password: "", role: "view_only" });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!editingUser && (!form.password || form.password.length < 6)) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await updateUser(editingUser.id, payload);
        toast.success("User updated");
      } else {
        await createUser(form);
        toast.success("User created");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}? They will lose access.`)) return;
    try {
      await deleteUser(u.id);
      toast.success("User removed");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (currentUser?.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="users-no-access">
        <Shield className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-lg font-semibold text-slate-700">Admin Access Required</p>
        <p className="text-sm text-slate-500 mt-1">Only the admin can manage users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Add users and control access rights
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-l-4 border-l-brand-navy bg-slate-50">
        <CardContent className="p-3 sm:p-4">
          <p className="text-sm text-slate-600">
            <strong>Full Access</strong> — Can view and edit all financial data.{" "}
            <strong>View Only</strong> — Can browse dashboards and reports, but cannot add, edit, or delete anything.
          </p>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-brand-orange border-t-transparent rounded-full" />
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center py-16">
          <Users className="w-12 h-12 text-slate-300 mb-3" />
          <p className="text-base font-semibold text-slate-700">No users yet</p>
          <p className="text-sm text-slate-500 mt-1">Add users to share access to the dashboard.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u) => {
            const roleConf = ROLE_CONFIG[u.role] || ROLE_CONFIG.view_only;
            const RoleIcon = roleConf.icon;
            const isCurrentUser = u.id === currentUser?.id;
            return (
              <Card key={u.id} className="card-hover" data-testid={`user-card-${u.id}`}>
                <CardHeader className="flex flex-row items-start justify-between pb-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-brand-navy flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">
                        {(u.name || u.email).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold truncate">
                        {u.name || "Unnamed"}
                        {isCurrentUser && <span className="text-xs text-slate-400 ml-1">(you)</span>}
                      </CardTitle>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  {!isCurrentUser && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => handleDelete(u)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${roleConf.color}`}>
                      <RoleIcon className="w-3 h-3" />
                      {roleConf.label}
                    </span>
                    {u.created_at && (
                      <span className="text-xs text-slate-400">
                        Added {new Date(u.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Doe"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@example.com"
                disabled={!!editingUser}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{editingUser ? "New Password (leave blank to keep)" : "Temporary Password"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingUser ? "Leave blank to keep current" : "Min 6 characters"}
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Access Level</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_access">
                    <div className="flex items-center gap-2">
                      <KeyRound className="w-3 h-3 text-emerald-600" />
                      Full Access — View & Edit
                    </div>
                  </SelectItem>
                  <SelectItem value="view_only">
                    <div className="flex items-center gap-2">
                      <Eye className="w-3 h-3 text-slate-500" />
                      View Only — Read-only
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
                data-testid="user-save-btn"
              >
                {saving ? "Saving..." : editingUser ? "Update User" : "Create User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
